import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import adminApi, { Page, SubscriberSummary, SubscriptionPlan } from '../../api/admin';
import { useLanguage } from '../../contexts/LanguageContext';

const PLAN_COLORS: Record<SubscriptionPlan, string> = {
  STARTER: 'bg-slate-100 text-slate-700',
  PRO: 'bg-blue-100 text-blue-700',
  BUSINESS: 'bg-indigo-100 text-indigo-700',
};

const SubscriberList: React.FC = () => {
  const { t } = useLanguage();
  const [data, setData] = useState<Page<SubscriberSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | ''>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setLoading(true);
    adminApi
      .listSubscribers({ plan: plan || undefined, q: q || undefined, page, size: 20 })
      .then(setData)
      .catch((e) => setError(e?.message ?? 'Failed'))
      .finally(() => setLoading(false));
  }, [plan, q, page]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">
          {t.admin?.subscribers?.title ?? 'Abonnés'}
        </h1>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder={t.admin?.subscribers?.searchPlaceholder ?? 'Raison sociale, matricule fiscal, email…'}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <select
            value={plan}
            onChange={(e) => {
              setPlan(e.target.value as SubscriptionPlan | '');
              setPage(0);
            }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">{t.admin?.subscribers?.allPlans ?? 'Tous les plans'}</option>
            <option value="STARTER">STARTER</option>
            <option value="PRO">PRO</option>
            <option value="BUSINESS">BUSINESS</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-slate-500">{t.common.loading}</div>}
      {error && <div className="text-red-600">{error}</div>}

      {data && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    {t.admin?.subscribers?.col?.name ?? 'Raison sociale'}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    {t.admin?.subscribers?.col?.mf ?? 'Matricule fiscal'}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    {t.admin?.subscribers?.col?.plan ?? 'Plan'}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Statut</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600">
                    {t.admin?.subscribers?.col?.invoicesMonth ?? 'Factures (mois)'}
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-600">
                    {t.admin?.subscribers?.col?.users ?? 'Utilisateurs'}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">
                    {t.admin?.subscribers?.col?.createdAt ?? 'Inscrit le'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.content.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      {t.common.noResults}
                    </td>
                  </tr>
                )}
                {data.content.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/subscribers/${s.id}`}
                        className="font-medium text-indigo-600 hover:text-indigo-700"
                      >
                        {s.raisonSociale}
                      </Link>
                      {s.email && <div className="text-xs text-slate-500">{s.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{s.matriculeFiscal}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${PLAN_COLORS[s.plan]}`}>
                        {s.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {s.actif === false ? (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800">Suspendu</span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-800">Actif</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{s.invoicesThisMonth}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{s.usersCount}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
              <div className="text-slate-500">
                {t.common.page} {data.number + 1} {t.common.of} {data.totalPages} —{' '}
                {data.totalElements} {t.common.total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={data.number === 0}
                  className="px-3 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  {t.common.previous}
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={data.number + 1 >= data.totalPages}
                  className="px-3 py-1 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  {t.common.next}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriberList;
